<script setup lang="ts">
  import CajaMovementsService from '@/api/services/caja-movements.service';
  import { useUi } from '@/composition/use-ui';
  import { ICajaMovement } from '@/interfaces/ICajaMovement';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';

  const props = defineProps<{
    cajaId: number;
    statusId: number;
    cajaName?: string;
    openDate?: string | null;
    closeDate?: string | null;
  }>();

  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const search = ref('');
  const movements = ref<ICajaMovement[] | null>(null);

  const typeLabelKeys: Record<ICajaMovement['type'], string> = {
    open: 'v2Stats.cajaMovements.typeOpen',
    close: 'v2Stats.cajaMovements.typeClose',
    inflow: 'v2Stats.cajaMovements.typeInflow',
    outflow: 'v2Stats.cajaMovements.typeOutflow',
  };

  const paymentMethodsMap = Globals.shortPaymentMethods.reduce<
    Record<number, string>
  >((acc, method) => {
    acc[method.id] = method.title;
    return acc;
  }, {});

  const title = computed(() => {
    const cajaPart = props.cajaName ? ` — ${props.cajaName}` : '';
    return `${i18nT('v2Stats.cajaMovements.title')}${cajaPart} #${
      props.statusId
    }`;
  });

  const subtitle = computed(() => {
    if (!props.openDate && !props.closeDate) return null;

    const open = props.openDate ? formatDateTime(props.openDate) : '—';
    const close = props.closeDate ? formatDateTime(props.closeDate) : '—';

    return String(
      proxy.$t('v2Stats.cajaMovements.range', { from: open, to: close })
    );
  });

  const totals = computed(() => {
    const items = movements.value ?? [];

    let inflows = 0;
    let outflows = 0;

    for (const m of items) {
      if (m.type === 'inflow') inflows += m.amount;
      if (m.type === 'outflow') outflows += m.amount;
    }

    return {
      count: items.length,
      inflows,
      outflows,
      net: inflows - outflows,
    };
  });

  const headers = computed(() => [
    { text: i18nT('v2Stats.cajaMovements.colDate'), value: 'createdAt' },
    { text: i18nT('v2Stats.cajaMovements.colUser'), value: 'user.name' },
    { text: i18nT('v2Stats.cajaMovements.colType'), value: 'type' },
    { text: i18nT('v2Stats.cajaMovements.colNote'), value: 'description' },
    {
      text: i18nT('v2Stats.cajaMovements.colPayment'),
      value: 'paymentMethod',
    },
    { text: i18nT('v2Stats.cajaMovements.colAmount'), value: 'amount' },
  ]);

  function formatPrice(price: number, space = true) {
    const symbol = restaurantStore.currency?.symbol ?? '';
    return symbol + (space && symbol ? ' ' : '') + displayPrice(price);
  }

  function formatDateTime(date: string | Date) {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getTypeLabel(type: ICajaMovement['type']) {
    const key = typeLabelKeys[type];
    return key ? i18nT(key) : type;
  }

  function getPaymentMethodLabel(
    paymentMethod: ICajaMovement['paymentMethod']
  ) {
    if (!paymentMethod) return '';
    return paymentMethodsMap[paymentMethod] ?? '';
  }

  function getNote(movement: ICajaMovement) {
    return movement.description || movement.comment || '—';
  }

  function formatSignedAmount(movement: ICajaMovement) {
    const sign = movement.type === 'outflow' ? '-' : '';
    return `${sign}${formatPrice(movement.amount)}`;
  }

  function getItemClass(item: ICajaMovement) {
    return {
      outflow: item.type === 'outflow',
      inflow: item.type === 'inflow',
      open: item.type === 'open',
      close: item.type === 'close',
      reimbursed: item.reimbursed,
    };
  }

  async function loadMovements() {
    if (!restaurantStore.restaurant || !restaurantStore.selectedBranch) {
      movements.value = [];
      return;
    }

    movements.value = null;

    try {
      movements.value = await CajaMovementsService.getAll(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        props.cajaId,
        props.statusId
      );
    } catch (e) {
      ui.serverError(e, i18nT('v2Stats.cajaMovements.loadError'));
      movements.value = [];
    }
  }

  onMounted(() => {
    loadMovements().catch(() => {});
  });
</script>

<template>
  <v-card class="modal-caja-movements">
    <v-card-title class="modal-title">
      <div class="title-container">
        <v-icon class="title-icon">mdi-swap-vertical</v-icon>
        <span class="title-text">{{ title }}</span>
      </div>
    </v-card-title>
    <v-card-subtitle v-if="subtitle" class="modal-subtitle">{{
      subtitle
    }}</v-card-subtitle>

    <v-card-text class="modal-body">
      <div class="toolbar">
        <div class="summary" v-if="movements">
          <v-chip class="chip" label small>
            {{ $t('v2Stats.cajaMovements.countChip') }}:
            <b>{{ totals.count }}</b>
          </v-chip>
          <v-chip class="chip chip--success" label small>
            {{ $t('v2Stats.cajaMovements.inflowsChip') }}:
            <b>{{ formatPrice(totals.inflows) }}</b>
          </v-chip>
          <v-chip class="chip chip--error" label small>
            {{ $t('v2Stats.cajaMovements.outflowsChip') }}:
            <b>{{ formatPrice(totals.outflows) }}</b>
          </v-chip>
          <v-chip class="chip" label small>
            {{ $t('v2Stats.cajaMovements.netChip') }}:
            <b>{{ formatPrice(totals.net) }}</b>
          </v-chip>
        </div>

        <v-text-field
          v-model="search"
          class="search"
          dense
          outlined
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :placeholder="String($t('v2Stats.cajaMovements.searchPlaceholder'))"
        />
      </div>

      <template v-if="!movements">
        <v-progress-linear color="primary" indeterminate />
      </template>
      <template v-else-if="movements.length === 0">
        <v-alert color="grey" icon="mdi-information" dark text>
          {{ $t('v2Stats.cajaMovements.empty') }}
        </v-alert>
      </template>
      <template v-else>
        <div class="table-container">
          <v-data-table
            class="movements-table elevation-0 v-data-table--orange"
            :headers="headers"
            :items="movements"
            :search="search"
            dense
            :mobile-breakpoint="0"
            calculate-widths
            hide-default-footer
            :items-per-page="-1"
            :item-class="getItemClass"
          >
            <template #item.createdAt="{ item }">
              <span>{{ formatDateTime(item.createdAt) }}</span>
            </template>
            <template #item.type="{ item }">
              <span>{{ getTypeLabel(item.type) }}</span>
            </template>
            <template #item.description="{ item }">
              <span class="note">{{ getNote(item) }}</span>
            </template>
            <template #item.paymentMethod="{ item }">
              <span>{{ getPaymentMethodLabel(item.paymentMethod) }}</span>
            </template>
            <template #item.amount="{ item }">
              <span class="amount">{{ formatSignedAmount(item) }}</span>
            </template>
          </v-data-table>
        </div>
      </template>
    </v-card-text>

    <v-card-actions class="modal-actions">
      <v-spacer />
      <v-btn text @click="$emit('close')">{{ $t('close') }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .modal-caja-movements {
    @apply tw-rounded-lg;

    .modal-title {
      @apply tw-pb-2;

      .title-container {
        @apply tw-flex tw-items-center tw-space-x-2;
      }

      .title-icon {
        @apply tw-text-gray-600;
      }

      .title-text {
        @apply tw-font-semibold tw-text-base;
      }
    }

    .modal-subtitle {
      @apply tw-pt-0;
    }

    .modal-body {
      @apply tw-pt-2 tw-flex tw-flex-col;

      .toolbar {
        @apply tw-flex tw-flex-col tw-gap-2 tw-mb-2;
      }

      .summary {
        @apply tw-flex tw-flex-wrap tw-gap-2;

        .chip {
          &.chip--success {
            @apply tw-border tw-border-green-200;
          }

          &.chip--error {
            @apply tw-border tw-border-red-200;
          }
        }
      }

      .search {
        @apply tw-max-w-3xl;
      }

      .table-container {
        @apply tw-overflow-y-auto;
        max-height: 60vh;
      }

      .movements-table {
        ::v-deep {
          .amount {
            @apply tw-font-medium;
          }

          .note {
            @apply tw-text-gray-700;
          }

          .outflow {
            @apply tw-bg-red-100;

            .amount {
              @apply tw-text-red-800;
            }
          }

          .inflow {
            @apply tw-bg-green-100;

            .amount {
              @apply tw-text-green-800;
            }
          }

          .reimbursed {
            @apply tw-line-through;
          }
        }
      }
    }

    .modal-actions {
      @apply tw-pt-0;
    }
  }
</style>
